import React, { useState } from 'react';
import { User, Mail, Phone, Calendar, Clock, Users, PartyPopper } from 'lucide-react';
import { api } from '../config/api';

const Booking = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    event_type: 'Birthday Party',
    date: '',
    time: '',
    guests_count: ''
  });

  const [submitting, setSubmitting] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSuccessMsg('');
    setErrorMsg('');

    try {
      // Get logged-in user if available
      const user = JSON.parse(localStorage.getItem('user') || 'null');

      const bookingResponse = await api.post('/bookings.php', {
        user_id: user?.user_id || null,
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        event_type: formData.event_type,
        date: formData.date,
        time: formData.time,
        guests_count: formData.guests_count
      });

      if (bookingResponse.data.success) {
        setSuccessMsg('Booking submitted successfully! We will contact you shortly.');
        setFormData({
          name: '', email: '', phone: '',
          event_type: 'Birthday Party', date: '', time: '', guests_count: ''
        });
      } else {
        setErrorMsg(bookingResponse.data.message || 'Booking failed. Please try again.');
      }
    } catch (error) {
      console.error('Booking error:', error);
      setErrorMsg('Booking failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle = {
    padding: '14px 18px 14px 48px',
    borderRadius: '10px',
    border: '1px solid #e0e0e0',
    backgroundColor: '#faf8f5',
    fontSize: '0.95rem',
    transition: 'all 0.3s ease'
  };

  const iconStyle = {
    position: 'absolute',
    left: '16px',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#D4AF37',
    zIndex: 5,
    pointerEvents: 'none'
  };

  return (
    <div>
      {/* Booking Header */}
      <section className="section-padding text-center" style={{
        background: 'linear-gradient(rgba(26, 26, 26, 0.8), rgba(26, 26, 26, 0.9)), url("https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=1920&q=80")',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
        color: '#ffffff'
      }}>
        <div className="container">
          <span className="section-subtitle" style={{ color: '#D4AF37' }}>Reserve Your Experience</span>
          <h1 className="mt-3 mb-4" style={{ fontFamily: 'Playfair Display, serif', fontSize: '3.5rem', fontWeight: 600, color: '#ffffff' }}>
            Book Your Table
          </h1>
          <div className="gold-divider-center" style={{ background: '#D4AF37' }}></div>
          <p style={{ fontSize: '1.1rem', maxWidth: '600px', margin: '20px auto 0', color: '#D4AF37', lineHeight: 1.8 }}>
            Join us for an unforgettable dining experience. Book your table for special occasions, group events, or intimate gatherings.
          </p>
        </div>
      </section>

      {/* Booking Form */}
      <section className="section-padding" style={{
        background: 'linear-gradient(rgba(26, 26, 26, 0.88), rgba(26, 26, 26, 0.92)), url("https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=1920&q=80")',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed'
      }}>
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-8">
              <div className="glass-card p-5">
                <div className="text-center mb-5">
                  <PartyPopper size={36} color="#D4AF37" className="mb-3" />
                  <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: '2rem', fontWeight: 600 }}>
                    Reserve Your Table
                  </h2>
                  <p style={{ color: '#888', fontSize: '0.95rem', marginTop: '8px' }}>
                    Fill in the details below and we'll prepare everything for you
                  </p>
                </div>

                <form onSubmit={handleSubmit}>
                  {/* Name */}
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label" style={{ fontWeight: 600, fontSize: '0.85rem', color: '#444', letterSpacing: '0.5px' }}>Full Name</label>
                      <div style={{ position: 'relative' }}>
                        <User size={16} style={iconStyle} />
                        <input
                          type="text" className="form-control" name="name"
                          value={formData.name} onChange={handleChange} required
                          placeholder="John Doe"
                          style={inputStyle}
                        />
                      </div>
                    </div>

                    {/* Email */}
                    <div className="col-md-6">
                      <label className="form-label" style={{ fontWeight: 600, fontSize: '0.85rem', color: '#444', letterSpacing: '0.5px' }}>Email</label>
                      <div style={{ position: 'relative' }}>
                        <Mail size={16} style={iconStyle} />
                        <input
                          type="email" className="form-control" name="email"
                          value={formData.email} onChange={handleChange} required
                          placeholder="john@example.com"
                          style={inputStyle}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Phone */}
                  <div className="mt-3">
                    <label className="form-label" style={{ fontWeight: 600, fontSize: '0.85rem', color: '#444', letterSpacing: '0.5px' }}>Phone Number</label>
                    <div style={{ position: 'relative' }}>
                      <Phone size={16} style={iconStyle} />
                      <input
                        type="tel" className="form-control" name="phone"
                        value={formData.phone} onChange={handleChange} required
                        placeholder="+256 772 206129"
                        style={inputStyle}
                      />
                    </div>
                  </div>

                  {/* Event Type */}
                  <div className="mt-3">
                    <label className="form-label" style={{ fontWeight: 600, fontSize: '0.85rem', color: '#444', letterSpacing: '0.5px' }}>Event Type</label>
                    <div style={{ position: 'relative' }}>
                      <PartyPopper size={16} style={iconStyle} />
                      <select
                        className="form-select" name="event_type"
                        value={formData.event_type} onChange={handleChange}
                        style={{ ...inputStyle, appearance: 'auto' }}
                      >
                        <option value="Birthday Party">Birthday Party</option>
                        <option value="Group Dinner">Group Dinner</option>
                        <option value="Corporate Event">Corporate Event</option>
                        <option value="Family Gathering">Family Gathering</option>
                        <option value="Romantic Dinner">Romantic Dinner</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                  </div>

                  {/* Date & Time */}
                  <div className="row g-3 mt-1">
                    <div className="col-md-6">
                      <label className="form-label" style={{ fontWeight: 600, fontSize: '0.85rem', color: '#444', letterSpacing: '0.5px' }}>Date</label>
                      <div style={{ position: 'relative' }}>
                        <Calendar size={16} style={iconStyle} />
                        <input
                          type="date" className="form-control" name="date"
                          value={formData.date} onChange={handleChange} required
                          style={inputStyle}
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label" style={{ fontWeight: 600, fontSize: '0.85rem', color: '#444', letterSpacing: '0.5px' }}>Time</label>
                      <div style={{ position: 'relative' }}>
                        <Clock size={16} style={iconStyle} />
                        <input
                          type="time" className="form-control" name="time"
                          value={formData.time} onChange={handleChange} required
                          style={inputStyle}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Guests */}
                  <div className="mt-3">
                    <label className="form-label" style={{ fontWeight: 600, fontSize: '0.85rem', color: '#444', letterSpacing: '0.5px' }}>Number of Guests</label>
                    <div style={{ position: 'relative' }}>
                      <Users size={16} style={iconStyle} />
                      <input
                        type="number" className="form-control" name="guests_count"
                        value={formData.guests_count} onChange={handleChange}
                        min="1" required placeholder="How many guests?"
                        style={inputStyle}
                      />
                    </div>
                  </div>

                  {successMsg && (
                    <div style={{
                      backgroundColor: 'rgba(76,175,80,0.15)', color: '#4CAF50', padding: '14px 18px',
                      borderRadius: '10px', fontSize: '0.9rem', marginBottom: '20px', textAlign: 'center'
                    }}>
                      {successMsg}
                    </div>
                  )}

                  {errorMsg && (
                    <div style={{
                      backgroundColor: 'rgba(231,76,60,0.12)', color: '#e74c3c', padding: '14px 18px',
                      borderRadius: '10px', fontSize: '0.9rem', marginBottom: '20px', textAlign: 'center'
                    }}>
                      {errorMsg}
                    </div>
                  )}

                  <button
                    type="submit" disabled={submitting}
                    className="btn btn-gold w-100 mt-4"
                    style={{
                      padding: '16px 32px', fontSize: '0.95rem',
                      letterSpacing: '2px', borderRadius: '10px',
                      backgroundColor: submitting ? '#ccc' : undefined,
                      cursor: submitting ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {submitting ? 'Reserving...' : 'Reserve Now'}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Booking;
